import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Avatar, Button, Badge, Menu, Dropdown, Drawer } from 'antd';
import { UserOutlined, DownOutlined, MenuOutlined, MailOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router';
import { useLocation } from 'react-router-dom';
import newMsgSound from '../../utils/messageSound';
import { isAuthenticated } from '../../../services/auth';
import {
  viewUser,
  getUser,
  getUserNotifications,
  setMsgToUnread,
} from '../../../services/user';
import { useDispatch, useSelector } from 'react-redux';
import socket from '../../../utils/socket'; // Use o socket único
import './NavMenu.css';

const NavMenu = () => {
  const userInfo = useSelector((state) => state.buynsellUser);
  const dispatch = useDispatch();

  const [notifications, setNotifications] = useState([]);
  const [notificationCount, setNotificationCount] = useState(null);
  const [sellHover, setSellHover] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const { user, token } = isAuthenticated();
  const history = useHistory();
  const location = useLocation();

  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 992);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadUser = async () => {
    if (user) {
      let res = await viewUser(user._id);
      let userNotifications = await getUserNotifications(user._id);
      dispatch({
        type: 'USER_DETAILS',
        payload: res.data,
      });
      setNotifications(userNotifications.data.notifications);
      setNotificationCount(userNotifications.data.currentNotificationCount);
    }
  };

  useEffect(() => {
    loadUser();
  }, [dispatch]);

  useEffect(() => {
    if (user) {
      if (!socket.connected) {
        socket.connect();
        socket.emit('join', { userId: user._id });
      }

      socket.on('newMsgReceived', ({ newMsg }) => {
        if (window.location.pathname !== '/messages') {
          setUnreadMessages((prev) => prev + 1);
          newMsgSound(newMsg.senderName);
        }
      });

      socket.on('messagesRead', () => {
        setUnreadMessages(0);
      });
    }

    return () => {
      socket.off('newMsgReceived');
      socket.off('messagesRead');
    };
  }, [user]);

  useEffect(() => {
    if (location.pathname === '/messages') {
      setUnreadMessages(0);
      if (user) {
        socket.emit('messagesRead', { userId: user._id });
      }
    }
  }, [location, user]);

  const logout = () => {
    window.localStorage.removeItem('buynsell');
    history.push('/login');
    window.location.reload();
  };

  const handleSell = () => {
    if (isAuthenticated()) {
      history.push(`/add-product/${user._id}`);
    } else {
      history.push('/login');
    }
  };

  // Menu do usuário
  const userMenu = (
    <Menu>
      <Menu.Item key="dashboard">
        <Link to="/user/dashboard">Painel</Link>
      </Menu.Item>
      <Menu.Item key="logout" onClick={logout}>
        Sair
      </Menu.Item>
    </Menu>
  );

  // Itens do menu para mobile e desktop
  const navLinks = (isMobile) => {
    return (
      <>
        <Link
          className="nav-link"
          to="/"
        >
          Início
        </Link>
      </>
    )
  };

  // Usuário ou login/registro para mobile e desktop
  const userSection = user ? (
    <Dropdown overlay={userMenu} trigger={['click']} overlayStyle={{ zIndex: 1300 }}>
      <span
        className="user-dropdown"
        tabIndex={0}
        onKeyPress={(e) => {
          if (e.key === 'Enter' || e.key === ' ') e.preventDefault();
        }}
      >
        <Avatar
          className="avatar"
          icon={<UserOutlined />}
          src={
            user.photo
              ? `${process.env.REACT_APP_API.replace(/\/api$/, '')}${user.photo}`
              : '/default-profile.png'
          }
        />
        <span className="user-name">
          {userInfo && userInfo.name
            ? userInfo.name.split(' ')[0]
            : 'Usuário'}
        </span>
        <DownOutlined className="down-icon" />
      </span>
    </Dropdown>
  ) : (
    <>
      <Link
        to="/login"
        className="login-link"
      >
        Entrar
      </Link>
      {process.env.REACT_APP_ENABLE_USER_REGISTRATION === 'true' && (
        <Link
          to="/register"
          className="register-link"
        >
          Cadastrar-se
        </Link>
      )}
    </>
  );

  return (
    <nav className="navbar">
      <div className="container">
        {/* Logo à esquerda */}
        <Link
          className="navbar-brand d-flex align-items-center"
          to="/"
        >
          <img
            className="logo-img"
            src={process.env.REACT_APP_LOGO_PATH || "/logo.png"}
            alt="Logo"
            style={{ height: process.env.REACT_APP_LOGO_HEIGHT ? `${process.env.REACT_APP_LOGO_HEIGHT}px` : '50px' }}
          />
        </Link>

        {/* Desktop menu */}
        {!isMobile && (
          <>
            <div className="d-flex align-items-center social-icons">
              {navLinks()}
              <div style={{ display: 'flex', gap: '24px', alignItems: 'center' }}>
                <a href={process.env.REACT_APP_FACEBOOK_URL || "#"} className="icon" target="_blank" rel="noopener noreferrer">
                  <i className="fab fa-facebook-f"></i>
                </a>
                <a href={process.env.REACT_APP_INSTAGRAM_URL || "#"} className="icon" target="_blank" rel="noopener noreferrer">
                  <i className="fab fa-instagram"></i>
                </a>
              </div>
              {userSection}
            </div>
          </>
        )}

        {/* Mobile menu button */}
        {isMobile && (
          <>
            <button
              className="mobile-menu-btn"
              onClick={() => setDrawerVisible(true)}
              aria-label="Abrir menu"
            >
              <MenuOutlined />
            </button>
            <Drawer
              title={null}
              placement="right"
              onClose={() => setDrawerVisible(false)}
              visible={drawerVisible}
              bodyStyle={{ padding: 0, background: '#f8fafc' }}
              width={290}
              style={{ zIndex: 1400 }}
            >
              <div className="drawer-body">
                {/* User Info */}
                <div className="user-info">
                  <Avatar
                    size={64}
                    className="avatar avatar-drawer"
                    icon={<UserOutlined />}
                    src={`${process.env.REACT_APP_API.replace(/\/api$/, '')}${userInfo.photo}`}
                  />
                  <span className="user-name-drawer">
                    {userInfo && userInfo.name
                      ? userInfo.name.split(' ')[0]
                      : 'Bem-vindo'}
                  </span>
                  {user && (
                    <span className="user-email">
                      {userInfo && userInfo.email}
                    </span>
                  )}
                </div>

                {/* Navigation Links */}
                <div className="nav-links-drawer">
                  <Link
                    to="/"
                    className="nav-link-drawer"
                    onClick={() => setDrawerVisible(false)}
                  >
                    Início
                  </Link>

                </div>

                {/* User Actions */}
                <div className="user-actions">
                  {user ? (
                    <>
                      {/*<Link to="/messages"
                        className="user-action-link">
                        <Badge count={unreadMessages} size="small" offset={[0, 8]}>
                          <MailOutlined className="mail-icon" />
                        </Badge>
                        Mensagens
                      </Link>*/}
                      <Link
                        to="/user/dashboard"
                        className="user-action-link"
                        onClick={() => setDrawerVisible(false)}
                      >
                        Painel
                      </Link>
                      <Button
                        type="text"
                        className="logout-btn"
                        onClick={() => {
                          setDrawerVisible(false);
                          logout();
                        }}
                      >
                        Sair
                      </Button>
                    </>
                  ) : (
                    <>
                      <Link
                        to="/login"
                        className="user-action-link"
                        onClick={() => setDrawerVisible(false)}
                      >
                        Entrar
                      </Link>
                      {process.env.REACT_APP_ENABLE_USER_REGISTRATION === 'true' && (
                        <Link
                          to="/register"
                          className="user-action-link"
                          onClick={() => setDrawerVisible(false)}
                        >
                          Cadastrar-se
                        </Link>
                      )}
                    </>
                  )}
                </div>

                {/* Social Icons */}
                <div className="social-icons-drawer">
                  <a href={process.env.REACT_APP_FACEBOOK_URL || "#"} className="icon icon-drawer" target="_blank" rel="noopener noreferrer">
                    <i className="fab fa-facebook-f"></i>
                  </a>
                  <a href={process.env.REACT_APP_INSTAGRAM_URL || "#"} className="icon icon-drawer" target="_blank" rel="noopener noreferrer">
                    <i className="fab fa-instagram"></i>
                  </a>
                </div>
              </div>
            </Drawer>
          </>
        )}
      </div>
    </nav>
  );
};

export default NavMenu;
